import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Icon, Message, Table } from 'semantic-ui-react';
import {
  PosVendaConfirmacaoEnderecoFilaItemDto,
  PosVendaConfirmacaoEnderecoFormaContato,
  PosVendaConfirmacaoEnderecoOrigem,
  PosVendaEnderecoEntregaDto,
} from '../../../types/posVenda';
import {
  CONFIRMACAO_ENDERECO_CAMPOS,
  ETIQUETA_ENTREGA_DIVERGENCIA,
  ETIQUETA_MATCH,
  ROTULO_ORIGEM,
  badgeClassMatch,
  enderecoConfirmadoPreenchido,
  enderecoEntregaVazio,
  formatarEnderecoLinha,
} from '../../../utils/posVenda/posVendaConfirmacaoEnderecoUtils';
import {
  useConcluirConfirmacaoEnderecoMutation,
  useSalvarEnderecoConfirmadoMutation,
} from '../../../hooks/posVenda/usePosVendaConfirmacaoEndereco';

function mensagemDe(error: unknown): string {
  const err = error as { response?: { data?: unknown }; message?: string };
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') return data.message;
  if (err?.message) return err.message;
  return 'Não consegui concluir esta ação.';
}

interface ConfirmacaoEnderecoDetalheProps {
  item: PosVendaConfirmacaoEnderecoFilaItemDto;
  onAtualizado?: () => void;
}

function origemInicial(item: PosVendaConfirmacaoEnderecoFilaItemDto): PosVendaConfirmacaoEnderecoOrigem {
  return item.enderecoConfirmadoFinal?.origem ?? item.opcoes.find(o => o.origem !== 'Manual')?.origem ?? 'Manual';
}
function enderecoInicial(item: PosVendaConfirmacaoEnderecoFilaItemDto): PosVendaEnderecoEntregaDto {
  return item.enderecoConfirmadoFinal?.endereco ?? item.opcoes.find(o => o.origem === origemInicial(item))?.endereco ?? enderecoEntregaVazio();
}

const ConfirmacaoEnderecoDetalhe: React.FC<ConfirmacaoEnderecoDetalheProps> = ({ item, onAtualizado }) => {
  const salvar = useSalvarEnderecoConfirmadoMutation();
  const concluir = useConcluirConfirmacaoEnderecoMutation();

  const [origem, setOrigem] = useState<PosVendaConfirmacaoEnderecoOrigem>(
    origemInicial(item)
  );
  const [endereco, setEndereco] = useState<PosVendaEnderecoEntregaDto>(
    enderecoInicial(item)
  );
  const [formaConfirmacaoManual, setFormaConfirmacaoManual] = useState<
    PosVendaConfirmacaoEnderecoFormaContato | ''
  >(item.formaConfirmacaoManual ?? '');
  const [observacao, setObservacao] = useState(item.observacaoConclusao ?? '');
  const [erro, setErro] = useState('');
  const preservarEdicaoAposConflito = useRef<{ filaId: string; protocoloId: string } | null>(null);

  useEffect(() => {
    if (preservarEdicaoAposConflito.current?.filaId === item.filaId &&
        preservarEdicaoAposConflito.current?.protocoloId === item.protocoloId) return;
    preservarEdicaoAposConflito.current = null;
    setOrigem(origemInicial(item));
    setEndereco(enderecoInicial(item));
    setFormaConfirmacaoManual(item.formaConfirmacaoManual ?? '');
    setObservacao(item.observacaoConclusao ?? '');
    setErro('');
  }, [item]);

  const opcaoSelecionada = useMemo(
    () => item.opcoes.find((o) => o.origem === origem),
    [item.opcoes, origem]
  );

  const matchesExibicao = item.camposDivergentesAtuais ?? (item.camposDivergentes.length > 0 ? item.camposDivergentes : opcaoSelecionada?.matches ?? []);

  const escolherOrigem = (nova: PosVendaConfirmacaoEnderecoOrigem) => {
    setOrigem(nova);
    const opcao = item.opcoes.find((o) => o.origem === nova);
    if (opcao) setEndereco({ ...opcao.endereco });
  };

  const patchEndereco = (campo: keyof PosVendaEnderecoEntregaDto, valor: string) => {
    setEndereco((prev) => ({ ...prev, [campo]: valor }));
    if (origem !== 'Manual') setOrigem('Manual');
  };

  const tratarFalha = (falha: unknown) => {
    setErro(mensagemDe(falha));
    const erroHttp = falha as { response?: { status?: number }; status?: number } | null;
    if (erroHttp?.response?.status === 409 || erroHttp?.status === 409) {
      // Refresh tokens without replacing the operator's draft on refetch.
      preservarEdicaoAposConflito.current = { filaId: item.filaId, protocoloId: item.protocoloId };
      onAtualizado?.();
    }
  };

  const salvarEndereco = async () => {
    setErro('');
    if (!enderecoConfirmadoPreenchido({ origem, endereco })) {
      setErro('Preencha CEP, logradouro, número, bairro, cidade e UF.');
      return;
    }
    try {
      await salvar.mutateAsync({
        filaId: item.filaId,
        dto: {
          origem,
          endereco,
          formaConfirmacaoManual: formaConfirmacaoManual || null,
          concurrencyToken: item.filaConcurrencyToken,
        },
      });
      preservarEdicaoAposConflito.current = null;
      onAtualizado?.();
    } catch (falha) {
      tratarFalha(falha);
    }
  };

  const concluirTicket = async () => {
    setErro('');
    if (enderecoAlterado || !enderecoConfirmadoPreenchido(item.enderecoConfirmadoFinal) || item.filaConcurrencyToken === undefined) {
      setErro('Salve o endereço confirmado antes de concluir o ticket.');
      return;
    }
    try {
      await concluir.mutateAsync({
        protocoloId: item.protocoloId,
        dto: {
          forma: formaConfirmacaoManual === 'Cliente' ? 'ComCliente' : formaConfirmacaoManual || null,
          observacao: observacao.trim() || null,
          concurrencyToken: item.protocoloConcurrencyToken,
          filaConcurrencyToken: item.filaConcurrencyToken,
        },
      });
      preservarEdicaoAposConflito.current = null;
      onAtualizado?.();
    } catch (falha) {
      tratarFalha(falha);
    }
  };

  const divergenciaEntrada = item.enderecoEntregaDivergencia
    ? ETIQUETA_ENTREGA_DIVERGENCIA[item.enderecoEntregaDivergencia]
    : 'Comparação não disponível';

  const enderecoAlterado = !item.enderecoConfirmadoFinal ||
    (['cep', 'logradouro', 'numero', 'bairro', 'cidade', 'uf', 'complemento', 'referencia'] as const)
      .some(campo => String(endereco[campo] ?? '') !== String(item.enderecoConfirmadoFinal?.endereco[campo] ?? ''));
  const podeConcluir = item.situacao === 'aberto' && !enderecoAlterado && !!item.protocoloId &&
    item.protocoloConcurrencyToken !== undefined && item.filaConcurrencyToken !== undefined && enderecoConfirmadoPreenchido(item.enderecoConfirmadoFinal);

  return (
    <div className="confirmacao-endereco-detalhe">
      {(item.ticketResumo || item.slaResumo) && (
        <p className="envios-card__meta" style={{ marginBottom: '0.45rem' }}>
          {item.ticketResumo}
          {item.ticketResumo && item.slaResumo ? ' · ' : ''}
          {item.slaResumo}
        </p>
      )}

      {item.gettelAlinhadoComVtmeNaEntrada === false && (
        <Message info size="small">
          Na entrada na fila, Gettel e VTME <strong>não estavam alinhados</strong> no endereço de entrega.
        </Message>
      )}

      {item.enderecoFinalDivergeDoContratoGettel && (
        <Message warning size="small">
          <Message.Header>
            <Icon name="warning sign" /> Endereço final diverge do contrato Gettel
          </Message.Header>
          <p>O endereço confirmado não coincide com o cadastro do contrato.</p>
        </Message>
      )}

      <p className="envios-card__meta" style={{ marginBottom: '0.5rem' }}>
        Entrada fila — {divergenciaEntrada}
      </p>

      {item.comparacaoAtualDivergencia && (
        <p className="envios-card__meta">Comparação atual — {ETIQUETA_ENTREGA_DIVERGENCIA[item.comparacaoAtualDivergencia]}</p>
      )}
      <Form>
        <Form.Group inline>
          {(['Gettel', 'Vtme', 'Manual'] as const).map((op) => (
            <Form.Radio
              key={op}
              label={ROTULO_ORIGEM[op]}
              disabled={!item.opcoes.some(o => o.origem === op)}
              checked={origem === op}
              onChange={() => escolherOrigem(op)}
            />
          ))}
        </Form.Group>

        <div className="confirmacao-endereco-grid">
          {[...CONFIRMACAO_ENDERECO_CAMPOS, { campo: 'referencia' as const, label: 'Referência' }].map(({ campo, label }) => (
            <Form.Input
              key={campo}
              label={label}
              value={String(endereco[campo] ?? '')}
              onChange={(_, data) => patchEndereco(campo, String(data.value))}
              readOnly={origem !== 'Manual'}
            />
          ))}
        </div>

        <p className="envios-card__endereco" style={{ marginTop: '0.35rem' }}>
          Prévia: {formatarEnderecoLinha(endereco)}
        </p>

        {matchesExibicao.length > 0 && (
          <Table compact size="small" className="confirmacao-endereco-match-table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Campo ({item.camposDivergentesAtuais ? 'atual' : 'na entrada'})</Table.HeaderCell>
                <Table.HeaderCell>Gettel</Table.HeaderCell>
                <Table.HeaderCell>VTME</Table.HeaderCell>
                <Table.HeaderCell>Match</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {matchesExibicao.map((m) => (
                <Table.Row key={m.campo}>
                  <Table.Cell>{m.label ?? m.campo}</Table.Cell>
                  <Table.Cell>{m.valorGettel ?? '—'}</Table.Cell>
                  <Table.Cell>{m.valorVtme ?? '—'}</Table.Cell>
                  <Table.Cell>
                    <span className={badgeClassMatch(m.match)}>{ETIQUETA_MATCH[m.match]}</span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

        <Button primary size="small" type="button" loading={salvar.isPending} disabled={item.filaConcurrencyToken === undefined || concluir.isPending} onClick={() => void salvarEndereco()}>
          <Icon name="save" /> Salvar endereço confirmado
        </Button>
      </Form>

      {item.historicoEnderecoConfirmado.length > 0 && (
        <div className="confirmacao-endereco-historico">
          <strong>Histórico de edições</strong>
          <ul>
            {item.historicoEnderecoConfirmado.map((h, index) => (
              <li key={h.id || index}>
                <span className="envios-card__meta">
                  {h.alteradoPorNome ?? h.alteradoPorUserId ?? 'Autor não informado'} · {h.alteradoEm ? new Date(h.alteradoEm).toLocaleString('pt-BR') : 'Data não informada'}
                </span>
                <div style={{ fontSize: '0.85rem' }}>
                  {(h.resumoAnterior || h.enderecoAnterior) && (
                    <div>
                      <em>Anterior:</em> {h.resumoAnterior ?? formatarEnderecoLinha(h.enderecoAnterior!)}
                      {h.origemAnterior ? ` (${ROTULO_ORIGEM[h.origemAnterior]})` : ''}
                    </div>
                  )}
                  <div>
                    <em>Novo:</em>{' '}
                    {h.resumoNovo ?? formatarEnderecoLinha(h.enderecoNovo ?? enderecoEntregaVazio())}
                    {h.origemNova ? ` (${ROTULO_ORIGEM[h.origemNova]})` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {item.situacao === 'aberto' && (
        <Form style={{ marginTop: '0.75rem' }}>
          <Form.Group inline>
            <label style={{ marginRight: '0.5rem' }}>Forma (opcional)</label>
            <Form.Radio
              label="Cliente"
              checked={formaConfirmacaoManual === 'Cliente'}
              onChange={() => setFormaConfirmacaoManual('Cliente')}
            />
            <Form.Radio
              label="Interno"
              checked={formaConfirmacaoManual === 'Interno'}
              onChange={() => setFormaConfirmacaoManual('Interno')}
            />
            <Form.Radio label="—" checked={!formaConfirmacaoManual} onChange={() => setFormaConfirmacaoManual('')} />
          </Form.Group>
          <Form.TextArea
            label="Observação (opcional)"
            rows={2}
            value={observacao}
            onChange={(_, d) => setObservacao(String(d.value))}
          />
          <Button
            color="green"
            size="small"
            type="button"
            disabled={!podeConcluir || salvar.isPending || concluir.isPending}
            loading={concluir.isPending}
            onClick={() => void concluirTicket()}
          >
            <Icon name="check circle" /> Concluir ticket
          </Button>
          {!podeConcluir && (
            <p className="envios-card__meta" style={{ marginTop: '0.35rem' }}>
              Para concluir, salve as alterações e confirme um endereço válido.
            </p>
          )}
        </Form>
      )}

      {item.situacao === 'concluido' && (
        <Message positive size="small">
          Ticket concluído
          {item.concluidoEm ? ` em ${new Date(item.concluidoEm).toLocaleString('pt-BR')}` : ''}.
          Você ainda pode corrigir o endereço salvo; as alterações ficam registradas no histórico.
        </Message>
      )}

      {erro && <Message negative size="small" content={erro} />}
    </div>
  );
};

export default ConfirmacaoEnderecoDetalhe;
